/**
 * Simple universal preview content replacement system
 * 
 * This module provides a simple mechanism to replace any Optimizely content
 * with preview content stored in session storage if there's an ID match.
 */

const PREVIEW_STORAGE_KEY = 'optimizely_preview_data'
const PROJECT_ID_KEY = 'optimizely_preview_project_id'
const CACHE_BYPASS_KEY = 'optimizely_cache_bypass'

// CMS cache-bypass flag. Set via ?preview=true on any URL, cleared via
// ?preview=false. Read by use-optimizely-page to thread `preview=true` onto
// the /optimizely-page fetch so SSR skips the LRU cache. We read the cookie
// (not sessionStorage) so newly-opened tabs in the same browser inherit the
// bypass — PWA Kit strips the Cookie header server-side, so the signal has to
// be promoted to a URL param on the client before fetching /optimizely-page.
export const isCacheBypassActive = () => {
    if (typeof window === 'undefined') return false
    try {
        return document.cookie
            .split(';')
            .some((c) => c.trim() === `${CACHE_BYPASS_KEY}=1`)
    } catch {
        return false
    }
}

export const setCacheBypass = (active) => {
    if (typeof window === 'undefined') return
    try {
        if (active) {
            document.cookie = `${CACHE_BYPASS_KEY}=1; path=/; SameSite=Lax`
        } else {
            document.cookie = `${CACHE_BYPASS_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
        }
    } catch {
        // document.cookie unavailable — silent no-op
    }
}

/**
 * Find matching key in an Optimizely object for a CMS camelCase key.
 * Handles GraphQL aliases like PanelCtaBlockCtas: Ctas — CMS has 'ctas',
 * PascalCase is 'Ctas', but Optimizely key is 'PanelCtaBlockCtas'.
 */
const findMatchingKey = (obj, camelKey) => {
    const pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1)
    if (pascalKey in obj) return pascalKey
    // Check for aliased keys that end with the PascalCase version
    for (const k of Object.keys(obj)) {
        if (k !== pascalKey && k.endsWith(pascalKey)) return k
    }
    return null
}

/**
 * Merge a single preview value into the original Optimizely value.
 * Handles ContentLink references, arrays (by index), and plain objects.
 */
const mergePreviewValue = (originalValue, previewValue) => {
    // Primitives / null — just use preview value
    if (previewValue === null || typeof previewValue !== 'object') {
        return previewValue
    }

    // Arrays — merge by index, preserving Optimizely structure per item
    if (Array.isArray(previewValue) && Array.isArray(originalValue)) {
        return originalValue.map((item, index) => {
            if (index < previewValue.length && previewValue[index] && typeof previewValue[index] === 'object') {
                return mergePreviewIntoBlock(item, previewValue[index])
            }
            return item
        })
    }

    // If original is a ContentLink reference with Expanded, merge into Expanded
    if (originalValue && typeof originalValue === 'object' && originalValue.Expanded) {
        return {
            ...originalValue,
            Expanded: mergePreviewIntoBlock(originalValue.Expanded, previewValue)
        }
    }

    // Both are plain objects — do a camelCase→PascalCase merge
    if (originalValue && typeof originalValue === 'object' && !Array.isArray(originalValue)) {
        return mergePreviewIntoBlock(originalValue, previewValue)
    }

    return previewValue
}

/**
 * Merge a flat CMS preview object (camelCase) into an Optimizely block (PascalCase).
 * Handles GraphQL aliases (e.g., PanelCtaBlockCtas for Ctas).
 */
const mergePreviewIntoBlock = (original, preview) => {
    if (!original || !preview) return original
    const result = {...original}
    for (const [key, value] of Object.entries(preview)) {
        // Skip structural/metadata fields that would break Optimizely GraphQL structure
        if (key === 'type' || key === 'status' || key === 'guid' || key === 'metadata' ||
            key === 'id' || key === 'contentLink' || key === 'contentType' || key === 'name') continue
        const matchKey = findMatchingKey(result, key)
        if (matchKey) {
            result[matchKey] = mergePreviewValue(result[matchKey], value)
        }
    }
    return result
}

/**
 * Apply preview block replacements to any Optimizely content
 * @param {*} content - Any Optimizely content (object, array, or primitive)
 * @returns {*} Content with preview blocks applied if matches found
 */
export const applyPreviewBlocks = (content) => {
    if (typeof window === 'undefined') {
        return content
    }

    try {
        const previewData = sessionStorage.getItem(PREVIEW_STORAGE_KEY)
        if (!previewData) {
            return content
        }

        const parsed = JSON.parse(previewData)
        const previewBlocks = parsed.blocks || []

        // Step 1: Block-level merges (field values)
        let result = content
        if (previewBlocks.length > 0) {
            const previewBlocksById = {}
            const indexBlock = (block) => {
                if (!block || typeof block !== 'object') return
                if (block.id) previewBlocksById[block.id] = block
                for (const value of Object.values(block)) {
                    if (Array.isArray(value)) {
                        value.forEach(item => {
                            if (item && typeof item === 'object' && item.id) indexBlock(item)
                        })
                    }
                }
            }
            previewBlocks.forEach(indexBlock)
            result = replaceContent(result, previewBlocksById)
        }

        // Step 2: Page structure (reorder/remove using existing GraphQL entries)
        const previewPages = parsed.pages || []
        if (previewPages.length > 0) {
            result = applyPreviewPages(result, previewPages)
        }

        return result
    } catch (error) {
        console.error('[Preview] ERROR applying preview:', error)
        return content
    }
}

/**
 * Recursively replace content blocks that match preview block IDs
 * @param {*} content - Content to process
 * @param {Object} previewBlocksById - Preview blocks indexed by ID
 * @returns {*} Processed content
 */
const replaceContent = (content, previewBlocksById, mergedIds = new Set()) => {
    if (!content || typeof content !== 'object') {
        return content
    }

    if (Array.isArray(content)) {
        return content.map(item => replaceContent(item, previewBlocksById, mergedIds))
    }

    // Check if this content block has an ID that matches a preview block
    // Optimizely GraphQL blocks store IDs under ContentLink.Id
    const contentId = content.id || content.ContentLink?.Id

    if (contentId && previewBlocksById[contentId] && !mergedIds.has(contentId)) {
        const previewBlock = previewBlocksById[contentId]
        mergedIds.add(contentId)

        // Merge preview (camelCase) values into original (PascalCase) structure
        const merged = mergePreviewIntoBlock(content, previewBlock)

        // Continue recursion on child properties (not re-matching this block)
        const processedMerged = {}
        for (const [key, value] of Object.entries(merged)) {
            processedMerged[key] = replaceContent(value, previewBlocksById, mergedIds)
        }
        return processedMerged
    }

    // No direct match, recursively process all properties
    const processedContent = {}
    for (const [key, value] of Object.entries(content)) {
        processedContent[key] = replaceContent(value, previewBlocksById, mergedIds)
    }

    return processedContent
}

/**
 * Use CMS page data to reorder/remove blocks in the GraphQL response.
 * Keeps existing GraphQL entries (correct structure for rendering).
 * New blocks that don't exist in GraphQL are skipped.
 */
const applyPreviewPages = (content, cmsPages) => {
    if (!content || !cmsPages || cmsPages.length === 0) return content

    const cmsPagesById = {}
    for (const page of cmsPages) {
        if (page.id) cmsPagesById[page.id] = page
    }

    const areaMapping = {
        mainRegion: 'MainRegion',
        mainContentArea: 'MainContentArea'
    }

    const items = content.items
    if (!items || !Array.isArray(items)) return content

    const newItems = items.map(item => {
        const pageId = item?.ContentLink?.Id
        const cmsPage = pageId ? cmsPagesById[pageId] : null
        if (!cmsPage) return item

        const result = {...item}
        for (const [cmsKey, graphqlKey] of Object.entries(areaMapping)) {
            const cmsArea = cmsPage[cmsKey]
            const graphqlArea = result[graphqlKey]
            if (!cmsArea || !Array.isArray(cmsArea) || !Array.isArray(graphqlArea)) continue

            // Index existing GraphQL entries by ContentLink.Id
            const graphqlEntriesById = {}
            for (const entry of graphqlArea) {
                const entryId = entry?.ContentLink?.Id || entry?.ContentLink?.Expanded?.ContentLink?.Id
                if (entryId) graphqlEntriesById[entryId] = entry
            }

            // Rebuild in CMS order, keeping only blocks that exist in GraphQL
            const newArea = []
            for (const cmsBlock of cmsArea) {
                const existing = graphqlEntriesById[cmsBlock.id]
                if (existing) {
                    newArea.push(existing)
                }
            }
            result[graphqlKey] = newArea
        }
        return result
    })

    return {...content, items: newItems}
}

/**
 * Check if preview mode is active (has preview data in session storage)
 * @returns {boolean} True if preview data exists
 */
export const isPreviewActive = () => {
    if (typeof window === 'undefined') {
        return false
    }
    
    try {
        const previewData = sessionStorage.getItem(PREVIEW_STORAGE_KEY)
        return previewData !== null && previewData !== undefined
    } catch (error) {
        return false
    }
}

/**
 * Get preview project ID from session storage
 * @returns {string|null} Project ID or null
 */
export const getPreviewProjectId = () => {
    if (typeof window === 'undefined') {
        return null
    }
    
    try {
        return sessionStorage.getItem(PROJECT_ID_KEY)
    } catch (error) {
        return null
    }
}

/**
 * Get preview data from session storage (for compatibility with existing code)
 * @returns {Object|null} Preview data or null
 */
export const getPreviewData = () => {
    if (typeof window === 'undefined') {
        return null
    }
    
    try {
        const previewData = sessionStorage.getItem(PREVIEW_STORAGE_KEY)
        return previewData ? JSON.parse(previewData) : null
    } catch (error) {
        return null
    }
}

/**
 * Clear preview data from session storage
 */
export const clearPreviewData = () => {
    if (typeof window === 'undefined') {
        return
    }
    
    try {
        sessionStorage.removeItem(PREVIEW_STORAGE_KEY)
        sessionStorage.removeItem(PROJECT_ID_KEY)
    } catch (error) {
        console.error('Error clearing preview data:', error)
    }
}

/**
 * Store preview data (for compatibility with existing preview selector)
 * @param {Object} data - Preview data to store
 * @param {string} projectId - Project ID
 */
export const storePreviewData = (data, projectId) => {
    try {
        sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(data))
        sessionStorage.setItem(PROJECT_ID_KEY, projectId)
    } catch (error) {
        console.error('Error storing preview data:', error)
    }
}

/**
 * Get language code (for compatibility - simplified implementation)
 * @param {string} locale - Locale string
 * @returns {string} Language code
 */
export const getLanguageCode = (locale) => {
    return locale?.split('-')[0] || 'en'
}
